import React, { ReactNode, useEffect, useState } from "react"
import { StaticContext } from "./staticContext"
import { VideoQueryType } from "../api/video"
import { useVideoAPI } from "../api/hooks/useVideoAPI"
import { useDirectAPI } from "../api/hooks/useDirectAPI"
import { DirectQueryType, DirectType, PagedDirectResponse } from "../api/direct"
import { httpClient } from "../http"
import axios from "axios"
import { usePlaylistAPI } from "../api/hooks/usePlaylistAPI"
import { PlaylistQueryType } from "../api/playlist"

export const StaticProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentDirect, setCurrentDirect] = useState<DirectType | null>(null)
  const [homeDisplay, setHomeDisplay] = useState<number>(0)
  const [currentDirectPosterSource, setCurrentDirectPosterSource] = useState<string | null>(null)
  const [homeDisplayLoading, setHomeDisplayLoading] = useState<boolean>(true)
  const [homeDisplayError, setHomeDisplayError] = useState<{ status: number } | null>(null)
  const [motd, setMotd] = useState<string | null>(null)
  const [motdLoading, setMotdLoading] = useState<boolean>(true)
  const [motdError, setMotdError] = useState<{ status: number } | null>(null)
  const {
    videosData: trending,
    loading: trendingLoading,
    error: trendingError,
    refresh: reloadTrendingVideos,
  } = useVideoAPI({ constraint: VideoQueryType.TRENDING })
  const {
    videosData: latestVideos,
    loading: latestVideosLoading,
    error: latestVideosError,
    refresh: reloadLatestVideos,
  } = useVideoAPI({ constraint: VideoQueryType.LATEST })
  const {
    directsData: latestDirects,
    loading: latestDirectsLoading,
    error: latestDirectsError,
    refresh: reloadLatestDirects,
  } = useDirectAPI({ constraint: DirectQueryType.LATEST })
  const {
    playlistsData: playlists,
    loading: playlistsLoading,
    error: playlistsError,
    refresh: reloadPlaylists,
  } = usePlaylistAPI({ constraint: PlaylistQueryType.ME })

  const reloadHomeDisplay = async () => {
    setHomeDisplayLoading(true)
    setHomeDisplayError(null)
    try {
      const { homeDisplay: hd } = await httpClient.instance.get<{ homeDisplay: number }>(`homeDisplay`)
      setHomeDisplay(hd)
      if (hd) {
        const response = await httpClient.instance.get<PagedDirectResponse>(`currentDirect`)
        const direct = (response.directs && response.directs[0]) || null
        setCurrentDirect(direct)
        if (direct) {
          try {
            const response = await fetch(`/api/media/poster/${direct.id}`, {
              credentials: "same-origin",
            })

            if (response.status === 200) {
              const blob = await response.blob()

              const posterURL = URL.createObjectURL(blob)
              setCurrentDirectPosterSource(posterURL)
            } else {
              setCurrentDirectPosterSource(null)
            }
          } catch (error) {
            console.error("Error while fetching poster :", error)
            setCurrentDirectPosterSource(null)
          }
        }
      } else {
        setCurrentDirect(null)
        setCurrentDirectPosterSource(null)
      }
    } catch (error) {
      if (axios.isAxiosError(error) && (error.status === 401 || error.status === 403)) {
        // Osef
      } else {
        console.error("Unexpected error while fetching home display or current direct:", error)
        setHomeDisplayError({ status: (axios.isAxiosError(error) && error.status) || 500 })
      }
    } finally {
      setHomeDisplayLoading(false)
    }
  }

  const reloadMotd = async () => {
    setMotdLoading(true)
    setMotdError(null)
    try {
      const { motd: message } = await httpClient.instance.get<{ motd: string }>(`motd`)
      setMotd(message)
    } catch (error) {
      if (axios.isAxiosError(error) && (error.status === 401 || error.status === 403)) {
        // Osef
      } else {
        console.error("Error while fetching MoTD :", error)
        setMotdError({ status: (axios.isAxiosError(error) && error.status) || 500 })
      }
    } finally {
      setMotdLoading(false)
    }
  }

  const reload = async () => {
    await Promise.all([
      reloadTrendingVideos(),
      reloadLatestVideos(),
      reloadLatestDirects(),
      reloadPlaylists(),
      reloadHomeDisplay(),
      reloadMotd(),
    ])
  }

  useEffect(() => {
    reload()
  }, [])

  const trendingVideos = trendingLoading ? [] : trending
  return (
    <StaticContext.Provider
      value={{
        trendingVideos,
        trendingLoading,
        trendingError,
        reloadTrendingVideos,
        latestVideos,
        latestVideosLoading,
        latestVideosError,
        reloadLatestVideos,
        latestDirects,
        latestDirectsLoading,
        latestDirectsError,
        reloadLatestDirects,
        homeDisplay,
        currentDirect,
        currentDirectPosterSource,
        homeDisplayLoading,
        homeDisplayError,
        reloadHomeDisplay,
        playlists,
        playlistsLoading,
        playlistsError,
        reloadPlaylists,
        motd,
        motdLoading,
        motdError,
        reloadMotd,
        loading: trendingLoading || latestVideosLoading || latestDirectsLoading || homeDisplayLoading,
        reload,
      }}
    >
      {children}
    </StaticContext.Provider>
  )
}
